import { html } from 'omni-ui';

/**
 * @typedef ToolbarTemplateValues
 * @property {Function} handleUndo - Handler for the undo button click
 * @property {Function} handleRedo - Handler for the redo button click
 * @property {boolean} canUndo - Flag for enabling/disabling the undo button
 * @property {boolean} canRedo - Flag for enabling/disabling the undo button
 * @property {Function} handleZoomIn - Handler for the zoom-in button click
 * @property {Function} handleZoomOut - Handler for the zoom-out button click
 * @property {Function} handleShowDetailsChange - Handler for the "View Details" toggle control that displays the legend
 * @property {boolean} showDetails - Value of the "View Details" toggle control. De-scoped for Phase 1.
 * @property {boolean} isReadonly - Flag for isReadonly user - used to enable/disable the toolbox button
 * @property {Function} handleOpenToolbox - Handler for the toolbox button click
 */

/**
 * A template for the blueprint toolbar that expects toolbar styles, as well as omni-styles, from the campaign-blueprint component.
 * @param {ToolbarTemplateValues} values - Various values for the template to use for handler assignment and display
 * @returns {TemplateResult}
 */
export const ToolbarTemplate = ({
  isReadonly,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
  handleZoomIn,
  handleZoomOut,
  handleRecenter,
  // showDetails,
  // handleShowDetailsChange,
  handleOpenToolbox,
}) => html`
  <omni-toolbar class="toolbar">
    <div class="toolbar-left" slot="start">
      <!-- TODO: De-scoped for Phase 1
        <omni-switch
          .checked={showDetails}
          @change={handleShowDetailsChange}
        >
          <span>View Details</span>
        </omni-switch>
        <div class="toolbar-divider mx-6"></div>
      -->
      <omni-tooltip>
        <button
          class="button is-outlined mr-3 zoom-button"
          @click=${handleZoomIn}
        >
          <omni-icon
            class="is-size-1"
            icon-id="omni:interactive:zoomIn"
          ></omni-icon>
        </button>
        <div slot="content">Zoom In</div>
      </omni-tooltip>
      <omni-tooltip>
        <button
          class="button is-outlined mr-3 zoom-button"
          @click=${handleZoomOut}
        >
          <omni-icon
            class="is-size-1"
            icon-id="omni:interactive:zoomOut"
          ></omni-icon>
        </button>
        <div slot="content">Zoom Out</div>
      </omni-tooltip>
      <omni-tooltip>
        <button
          slot="invoker"
          class="button is-outlined recenter-button"
          @click=${handleRecenter}
        >
          <omni-icon
            class="is-size-1"
            icon-id="omni:informative:home"
          ></omni-icon>
        </button>
        <div slot="content">Re-Center the canvas at 100% zoom</div>
      </omni-tooltip>
      <div class="mr-6"></div>
      <button
        class="button is-white mr-3 history-button"
        ?disabled=${!canUndo}
        @click=${handleUndo}
      >
        <omni-icon class="is-size-1" icon-id="omni:editor:undo"></omni-icon>
      </button>
      <button
        class="button is-white history-button"
        ?disabled=${!canRedo}
        @click=${handleRedo}
      >
        <omni-icon class="is-size-1" icon-id="omni:editor:redo"></omni-icon>
      </button>

      <div class="mr-6"></div>
      <omni-tooltip>
        <button slot="invoker" class="button is-white help-button">
          <omni-icon
            class="is-size-1"
            icon-id="omni:informative:help"
          ></omni-icon>
        </button>
        <div slot="content">
          <style>
            /* .omni class specificty also infects this slot */
            ul {
              list-style-type: ' - ' !important;
              list-style-position: inside !important;
            }
          </style>
          Objects interactions:
          <ul>
            <li>Double click: Edit text</li>
            <li>Right click: Edit/Clone/Delete</li>
            <li>Click &amp; drag on an object: Move object</li>
            <li>Alt+Click or Middle-Click &amp; drag: Pan canvas</li>
            <li>Click &amp; drag: Select object(s)</li>
          </ul>
        </div>
      </omni-tooltip>
      <omni-tooltip>
        <button
          class="button is-white"
          ?disabled=${isReadonly}
          @click=${handleOpenToolbox}
        >
          <omni-icon
            class="is-size-1"
            icon-id="omni:informative:shapes"
          ></omni-icon>
        </button>
        <div slot="content">Toolbox</div>
      </omni-tooltip>
    </div>
  </omni-toolbar>
`;
